import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Badge,
  Tabs,
  Tab,
  Divider,
  ListItemText,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AlertIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import EditIcon from '@mui/icons-material/Edit';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import kidsClubService from '../api/services/kidsClubService';
import memberService from '../api/services/memberService';
import {
  fetchCurrentCheckInsStart,
  fetchCurrentCheckInsSuccess,
  fetchCurrentCheckInsFailure,
  performCheckInStart,
  performCheckInSuccess,
  performCheckInFailure,
  performCheckOutStart,
  performCheckOutSuccess,
  performCheckOutFailure,
  updateParentLocationStart,
  updateParentLocationSuccess,
  updateParentLocationFailure,
  fetchUnresolvedAlertsStart,
  fetchUnresolvedAlertsSuccess,
  fetchUnresolvedAlertsFailure,
  createAlertStart,
  createAlertSuccess,
  createAlertFailure,
  resolveAlertStart,
  resolveAlertSuccess,
  resolveAlertFailure,
  clearCheckInError,
  clearCheckInSuccess,
  clearAlertError,
  clearAlertSuccess,
} from '../store/slices/kidsClubSlice';
import {
  KidsClubCheckInFormData,
  KidsClubCheckOutFormData,
  EmergencyAlert,
  EmergencyAlertFormData,
  AlertSeverity,
  AlertType,
  WalkInFormData,
} from '../types';
import { getErrorMessage } from '../api/axiosConfig';
import WeeklyCapacityView from '../components/KidsClub/WeeklyCapacityView';
import DailyBookingBreakdown from '../components/KidsClub/DailyBookingBreakdown';
import QuickDateFilters, { ViewMode } from '../components/KidsClub/QuickDateFilters';

const KidsClubStaffPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    currentCheckIns,
    unresolvedAlerts,
    checkInLoading,
    alertLoading,
    checkInError,
    alertError,
    checkInSuccess,
    alertSuccess,
  } = useAppSelector((state) => state.kidsClub);

  const [tabValue, setTabValue] = useState(0);
  const [openCheckInDialog, setOpenCheckInDialog] = useState(false);
  const [openCheckOutDialog, setOpenCheckOutDialog] = useState(false);
  const [openAlertDialog, setOpenAlertDialog] = useState(false);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [openEditCheckInDialog, setOpenEditCheckInDialog] = useState(false);
  const [editingCheckIn, setEditingCheckIn] = useState<any>(null);
  const [editCheckInForm, setEditCheckInForm] = useState<{
    current_parent_location: string;
    check_in_notes: string;
    checked_in_by_member_id: string;
    checked_in_at: string;
  }>({
    current_parent_location: '',
    check_in_notes: '',
    checked_in_by_member_id: '',
    checked_in_at: '',
  });
  const [openWalkInDialog, setOpenWalkInDialog] = useState(false);
  const [openResolveDialog, setOpenResolveDialog] = useState(false);
  const [openAddBookingDialog, setOpenAddBookingDialog] = useState(false);
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolvedAlerts, setResolvedAlerts] = useState<EmergencyAlert[]>([]);
  const [loadingResolvedAlerts, setLoadingResolvedAlerts] = useState(false);
  const [selectedCheckInId, setSelectedCheckInId] = useState<string | null>(null);
  const [selectedCheckIn, setSelectedCheckIn] = useState<any>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<string>('');
  const [todaysBookings, setTodaysBookings] = useState<any[]>([]);
  const [pendingBookings, setPendingBookings] = useState<any[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [loadingPendingBookings, setLoadingPendingBookings] = useState(false);
  const [walkInLoading, setWalkInLoading] = useState(false);
  const [walkInError, setWalkInError] = useState<string | null>(null);
  const [walkInSuccess, setWalkInSuccess] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [dateBookings, setDateBookings] = useState<any[]>([]);
  const [bookingSummary, setBookingSummary] = useState<any>(null);
  const [loadingDateBookings, setLoadingDateBookings] = useState(false);

  // Add Booking state
  const [addBookingMemberNumber, setAddBookingMemberNumber] = useState('');
  const [addBookingLookupResult, setAddBookingLookupResult] = useState<any>(null);
  const [addBookingLoading, setAddBookingLoading] = useState(false);
  const [addBookingLookupLoading, setAddBookingLookupLoading] = useState(false);
  const [addBookingError, setAddBookingError] = useState<string | null>(null);
  const [addBookingSuccess, setAddBookingSuccess] = useState(false);
  const [addBookingForm, setAddBookingForm] = useState({
    booking_date: '',
    dependent_ids: [] as string[], // Changed to array for multiple selection
    start_time: '',
    end_time: '',
    parent_location: '',
    special_instructions: '',
  });

  const [selectedBookingIds, setSelectedBookingIds] = useState<string[]>([]);
  const [checkInForm, setCheckInForm] = useState<Omit<KidsClubCheckInFormData, 'booking_id'>>({
    check_in_notes: '',
    parent_location: '',
    checked_in_by_member_id: '',
  });

  const [checkOutForm, setCheckOutForm] = useState<KidsClubCheckOutFormData>({
    activities_participated: [],
    meals_consumed: [],
    behavior_notes: '',
    incident_occurred: false,
    incident_details: '',
    check_out_notes: '',
    checked_out_by_member_id: '',
  });

  const [availableFamilyMembers, setAvailableFamilyMembers] = useState<any[]>([]);
  const [loadingFamilyMembers, setLoadingFamilyMembers] = useState(false);

  const [alertForm, setAlertForm] = useState<EmergencyAlertFormData>({
    booking_id: '',
    severity: AlertSeverity.INFO,
    alert_type: AlertType.OTHER,
    message: '',
    channels: ['call'],
  });

  const [locationUpdate, setLocationUpdate] = useState('');

  const [memberNumber, setMemberNumber] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [memberLookupResult, setMemberLookupResult] = useState<any>(null);
  const [walkInForm, setWalkInForm] = useState({
    member_id: '',
    dependent_ids: [] as string[], // Changed to array for multi-select
    parent_location: '',
    notes: '',
  });

  // Calendar state
  const [currentView, setCurrentView] = useState<ViewMode>('week');
  const [baseDate, setBaseDate] = useState<Date>(new Date());
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [detailedData, setDetailedData] = useState<any[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [calendarDetailedView, setCalendarDetailedView] = useState(false);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [expandedDayData, setExpandedDayData] = useState<any | null>(null);
  const [loadingExpanded, setLoadingExpanded] = useState(false);

  const availableActivities = [
    'Arts & Crafts',
    'Outdoor Play',
    'Board Games',
    'Reading',
    'Sports',
    'Music',
    'Movies',
    'Swimming',
  ];

  const availableMeals = ['Breakfast', 'Morning Snack', 'Lunch', 'Afternoon Snack', 'Dinner'];

  // Reject booking state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [bookingToReject, setBookingToReject] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Cancel booking state (staff)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<string | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');

  // Edit booking state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({
    booking_date: '',
    start_time: '',
    end_time: '',
    session: '',
    parent_location: '',
    special_instructions: '',
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Track initial load vs background refresh
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Check if any dialog is open (pause auto-refresh during user interaction)
  const isAnyDialogOpen = openCheckInDialog || openCheckOutDialog || openAlertDialog ||
                          openLocationDialog || openWalkInDialog || openResolveDialog ||
                          rejectDialogOpen || cancelDialogOpen || openAddBookingDialog || editDialogOpen;

  useEffect(() => {
    loadCurrentCheckIns(true);
    loadUnresolvedAlerts();
    loadResolvedAlerts(true);
    loadTodaysBookings(true);
    loadPendingBookings(true);

    // Mark initial load as complete after a short delay
    setTimeout(() => setIsInitialLoad(false), 1000);

    // Refresh every 30 seconds (silently in background)
    const interval = setInterval(() => {
      // Don't refresh if user has a dialog open
      if (!isAnyDialogOpen) {
        loadCurrentCheckIns(false);
        loadUnresolvedAlerts();
        loadResolvedAlerts(false);
        loadTodaysBookings(false);
        loadPendingBookings(false);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (checkInSuccess) {
      setOpenCheckInDialog(false);
      setOpenCheckOutDialog(false);
      setOpenLocationDialog(false);
      resetCheckInForm();
      resetCheckOutForm();
      setTimeout(() => dispatch(clearCheckInSuccess()), 3000);
    }
  }, [checkInSuccess]);

  useEffect(() => {
    if (alertSuccess) {
      setOpenAlertDialog(false);
      resetAlertForm();
      setTimeout(() => dispatch(clearAlertSuccess()), 3000);
    }
  }, [alertSuccess]);

  useEffect(() => {
    if (walkInSuccess) {
      setOpenWalkInDialog(false);
      resetWalkInForm();
      setTimeout(() => setWalkInSuccess(false), 3000);
    }
  }, [walkInSuccess]);

  useEffect(() => {
    if (addBookingSuccess) {
      setOpenAddBookingDialog(false);
      resetAddBookingForm();
      setTimeout(() => setAddBookingSuccess(false), 3000);
    }
  }, [addBookingSuccess]);

  useEffect(() => {
    loadBookingsByDate(selectedDate);
  }, [selectedDate]);

  // Load calendar data when tab 0 (Booking Calendar) is selected
  useEffect(() => {
    if (tabValue === 0) {
      fetchSummaryData(currentView);
    }
  }, [tabValue]);

  const loadCurrentCheckIns = async (showLoading = true) => {
    if (showLoading) dispatch(fetchCurrentCheckInsStart());
    try {
      // Get all of today's check-ins (both current and checked out)
      const checkIns = await kidsClubService.getTodaysCheckIns();
      dispatch(fetchCurrentCheckInsSuccess(checkIns));
    } catch (error) {
      if (showLoading) dispatch(fetchCurrentCheckInsFailure(getErrorMessage(error)));
    }
  };

  const loadUnresolvedAlerts = async () => {
    dispatch(fetchUnresolvedAlertsStart());
    try {
      const alerts = await kidsClubService.getUnresolvedAlerts();
      dispatch(fetchUnresolvedAlertsSuccess(alerts));
    } catch (error) {
      dispatch(fetchUnresolvedAlertsFailure(getErrorMessage(error)));
    }
  };

  const loadResolvedAlerts = async (showLoading = true) => {
    if (showLoading) setLoadingResolvedAlerts(true);
    try {
      const alerts = await kidsClubService.getResolvedAlertsToday();
      setResolvedAlerts(alerts);
    } catch (error) {
      console.error('Failed to load resolved alerts:', error);
    } finally {
      if (showLoading) setLoadingResolvedAlerts(false);
    }
  };

  const loadTodaysBookings = async (showLoading = true) => {
    if (showLoading) setLoadingBookings(true);
    try {
      // Fetch all bookings (pending + confirmed) for today
      const allBookings = await kidsClubService.getTodaysBookings();
      // Filter for pending and confirmed (exclude checked_in, checked_out, cancelled)
      const activeBookings = allBookings.filter((b: any) =>
        ['pending', 'confirmed'].includes(b.status)
      );
      setTodaysBookings(activeBookings);
    } catch (error) {
      console.error('Failed to load today\'s bookings:', error);
    } finally {
      if (showLoading) setLoadingBookings(false);
    }
  };

  const loadPendingBookings = async (showLoading = true) => {
    if (showLoading) setLoadingPendingBookings(true);
    try {
      // Fetch all pending bookings across all members (staff endpoint)
      const pending = await kidsClubService.getStaffPendingBookings(7);
      setPendingBookings(pending);
    } catch (error) {
      console.error('Failed to load pending bookings:', error);
    } finally {
      if (showLoading) setLoadingPendingBookings(false);
    }
  };

  const loadBookingsByDate = async (date: string) => {
    setLoadingDateBookings(true);
    try {
      const result = await kidsClubService.getBookingsByDate(date);
      setDateBookings(result.data);
      setBookingSummary(result.summary);
    } catch (error) {
      console.error('Failed to load bookings for date:', error);
    } finally {
      setLoadingDateBookings(false);
    }
  };

  // Calendar helper functions
  const getMonday = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const getDateRange = (view: ViewMode, date: Date = baseDate): { start: Date; end: Date } => {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    switch (view) {
      case 'day':
        return { start: targetDate, end: targetDate };

      case 'week': {
        const start = getMonday(targetDate);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        return { start, end };
      }

      case 'month': {
        const start = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
        const end = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
        return { start, end };
      }

      default:
        return { start: targetDate, end: new Date(targetDate.getTime() + 6 * 24 * 60 * 60 * 1000) };
    }
  };

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchSummaryData = async (view: ViewMode) => {
    setCalendarLoading(true);
    setCalendarError(null);

    try {
      const range = getDateRange(view, baseDate);
      const startDate = formatDate(range.start);
      const endDate = formatDate(range.end);

      const data = await kidsClubService.getBookingsSummary(startDate, endDate, true);
      setSummaryData(data);

      if (view === 'day') {
        const detailedData = await kidsClubService.getBookingsSummary(startDate, endDate, true);
        setDetailedData(detailedData);
        setCalendarDetailedView(true);
      } else {
        setDetailedData([]);
        setCalendarDetailedView(false);
      }
    } catch (err: any) {
      console.error('Error fetching booking summary:', err);
      setCalendarError(err.response?.data?.message || 'Failed to load booking summary');
      setSummaryData([]);
      setDetailedData([]);
    } finally {
      setCalendarLoading(false);
    }
  };

  const fetchDetailedDate = async (date: string) => {
    setCalendarLoading(true);
    setCalendarError(null);

    try {
      const data = await kidsClubService.getBookingsSummary(date, date, true);
      setDetailedData(data);
      setCalendarDetailedView(true);
    } catch (err: any) {
      console.error('Error fetching detailed booking data:', err);
      setCalendarError(err.response?.data?.message || 'Failed to load booking details');
    } finally {
      setCalendarLoading(false);
    }
  };

  // Fetch expanded day data for inline display
  const fetchExpandedDayData = async (date: string) => {
    setLoadingExpanded(true);
    setCalendarError(null);

    try {
      const data = await kidsClubService.getBookingsSummary(date, date, true);
      if (data && data.length > 0) {
        setExpandedDayData(data[0]);
      }
    } catch (err: any) {
      console.error('Error fetching expanded day data:', err);
      setCalendarError(err.response?.data?.message || 'Failed to load booking details');
    } finally {
      setLoadingExpanded(false);
    }
  };

  const handleViewChange = (view: ViewMode) => {
    setCurrentView(view);
    setBaseDate(new Date()); // Reset to today when changing views
    setExpandedDate(null);
    setExpandedDayData(null);
    fetchSummaryData(view);
  };

  const handleNavigate = (direction: 'prev' | 'next') => {
    const multiplier = direction === 'next' ? 1 : -1;
    const newDate = new Date(baseDate);

    // Context-aware navigation based on current view
    switch (currentView) {
      case 'day':
        newDate.setDate(newDate.getDate() + (1 * multiplier));
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + (7 * multiplier));
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + (1 * multiplier));
        break;
    }

    setBaseDate(newDate);
    setExpandedDate(null);
    setExpandedDayData(null);

    // Fetch data for the new date range
    const range = getDateRange(currentView, newDate);
    setCalendarLoading(true);
    setCalendarError(null);

    kidsClubService
      .getBookingsSummary(formatDate(range.start), formatDate(range.end), true)
      .then((data) => {
        setSummaryData(data);
        setDetailedData([]);
        setCalendarDetailedView(false);
      })
      .catch((err) => {
        console.error('Error fetching booking summary:', err);
        setCalendarError(err.response?.data?.message || 'Failed to load booking summary');
        setSummaryData([]);
      })
      .finally(() => setCalendarLoading(false));
  };

  const handleDateClick = (date: string) => {
    // In day view, switch to detailed view
    if (currentView === 'day') {
      fetchDetailedDate(date);
      return;
    }

    // In week/month view, toggle inline expansion
    if (expandedDate === date) {
      // Clicking the same date again collapses it
      setExpandedDate(null);
      setExpandedDayData(null);
    } else {
      // Expand new date
      setExpandedDate(date);
      fetchExpandedDayData(date);
    }
  };

  // Get overview label based on current view
  const getOverviewLabel = (): string => {
    switch (currentView) {
      case 'day': {
        const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
        return baseDate.toLocaleDateString('en-US', options);
      }
      case 'week':
        return 'Weekly Overview';
      case 'month': {
        const options: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' };
        return baseDate.toLocaleDateString('en-US', options);
      }
      default:
        return 'Overview';
    }
  };

  const handleConfirmBooking = async (bookingId: string) => {
    try {
      await kidsClubService.confirmBooking(bookingId);
      // Refresh the calendar data
      if (calendarDetailedView) {
        const date = detailedData[0]?.date;
        if (date) {
          fetchDetailedDate(date);
        }
      } else {
        fetchSummaryData(currentView);
      }
      // Also refresh today's bookings and pending bookings
      loadTodaysBookings();
      loadPendingBookings();

      // If there's an expanded date, refresh it
      if (expandedDate) {
        fetchExpandedDayData(expandedDate);
      }
    } catch (error: any) {
      console.error('Error confirming booking:', error);
      alert(error.response?.data?.message || 'Failed to confirm booking');
    }
  };

  const handleRejectBookingClick = (bookingId: string) => {
    setBookingToReject(bookingId);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectBookingConfirm = async () => {
    if (!bookingToReject || !rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      await kidsClubService.rejectBooking(bookingToReject, rejectionReason);
      setRejectDialogOpen(false);
      setBookingToReject(null);
      setRejectionReason('');

      // Refresh the calendar data
      if (calendarDetailedView) {
        const date = detailedData[0]?.date;
        if (date) {
          fetchDetailedDate(date);
        }
      } else {
        fetchSummaryData(currentView);
      }
      // Also refresh today's bookings if visible
      loadTodaysBookings();
      alert('Booking rejected successfully');
    } catch (error: any) {
      console.error('Error rejecting booking:', error);
      alert(error.response?.data?.message || 'Failed to reject booking');
    }
  };

  const handleRejectDialogClose = () => {
    setRejectDialogOpen(false);
    setBookingToReject(null);
    setRejectionReason('');
  };

  const handleCancelBookingClick = (bookingId: string) => {
    setBookingToCancel(bookingId);
    setCancellationReason('');
    setCancelDialogOpen(true);
  };

  const handleCancelBookingConfirm = async () => {
    if (!bookingToCancel || !cancellationReason.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }

    try {
      await kidsClubService.staffCancelBooking(bookingToCancel, cancellationReason);
      setCancelDialogOpen(false);
      setBookingToCancel(null);
      setCancellationReason('');

      // Refresh the calendar data
      if (calendarDetailedView) {
        const date = detailedData[0]?.date;
        if (date) {
          fetchDetailedDate(date);
        }
      } else {
        fetchSummaryData(currentView);
      }
      // Also refresh today's bookings and pending bookings
      loadTodaysBookings();
      loadPendingBookings();
      loadCurrentCheckIns();

      // If there's an expanded date, refresh it
      if (expandedDate) {
        fetchExpandedDayData(expandedDate);
      }

      alert('Booking cancelled successfully');
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      alert(error.response?.data?.message || 'Failed to cancel booking');
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setBookingToCancel(null);
    setCancellationReason('');
  };

  const handleReinstateBooking = async (bookingId: string) => {
    try {
      await kidsClubService.reinstateBooking(bookingId);
      // Refresh the calendar data
      if (calendarDetailedView) {
        const date = detailedData[0]?.date;
        if (date) {
          fetchDetailedDate(date);
        }
      } else {
        fetchSummaryData(currentView);
      }
      // Also refresh today's bookings
      loadTodaysBookings();

      // If there's an expanded date, refresh it
      if (expandedDate) {
        fetchExpandedDayData(expandedDate);
      }
    } catch (error: any) {
      console.error('Error reinstating booking:', error);
      alert(error.response?.data?.message || 'Failed to reinstate booking');
    }
  };

  const handleEditBooking = (booking: any) => {
    setBookingToEdit(booking);
    setEditForm({
      booking_date: booking.booking_date || '',
      start_time: booking.start_time || '',
      end_time: booking.end_time || '',
      session: booking.session || '',
      parent_location: booking.parent_location || '',
      special_instructions: booking.special_instructions || '',
    });
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setBookingToEdit(null);
    setEditForm({
      booking_date: '',
      start_time: '',
      end_time: '',
      session: '',
      parent_location: '',
      special_instructions: '',
    });
    setEditError(null);
    setEditLoading(false);
  };

  const handleEditSubmit = async () => {
    if (!bookingToEdit) return;

    setEditLoading(true);
    setEditError(null);

    try {
      // Only send fields that have changed
      const updateData: any = {};
      if (editForm.booking_date && editForm.booking_date !== bookingToEdit.booking_date) {
        updateData.booking_date = editForm.booking_date;
      }
      if (editForm.start_time && editForm.start_time !== bookingToEdit.start_time) {
        updateData.start_time = editForm.start_time;
      }
      if (editForm.end_time && editForm.end_time !== bookingToEdit.end_time) {
        updateData.end_time = editForm.end_time;
      }
      if (editForm.session && editForm.session !== bookingToEdit.session) {
        updateData.session = editForm.session;
      }
      if (editForm.parent_location !== undefined && editForm.parent_location !== bookingToEdit.parent_location) {
        updateData.parent_location = editForm.parent_location;
      }
      if (editForm.special_instructions !== undefined && editForm.special_instructions !== bookingToEdit.special_instructions) {
        updateData.special_instructions = editForm.special_instructions;
      }

      if (Object.keys(updateData).length === 0) {
        setEditError('No changes detected');
        return;
      }

      await kidsClubService.updateBooking(bookingToEdit.id, updateData);

      // Refresh data
      if (calendarDetailedView) {
        const date = detailedData[0]?.date;
        if (date) {
          fetchDetailedDate(date);
        }
      } else {
        fetchSummaryData(currentView);
      }
      loadTodaysBookings();
      loadPendingBookings();
      if (expandedDate) {
        fetchExpandedDayData(expandedDate);
      }

      handleCloseEditDialog();
    } catch (error: any) {
      console.error('Error updating booking:', error);
      setEditError(error.response?.data?.message || 'Failed to update booking');
    } finally {
      setEditLoading(false);
    }
  };

  const resetCheckInForm = () => {
    setSelectedBookingIds([]);
    setCheckInForm({
      check_in_notes: '',
      parent_location: '',
      checked_in_by_member_id: '',
    });
    setAvailableFamilyMembers([]);
  };

  const resetCheckOutForm = () => {
    setCheckOutForm({
      activities_participated: [],
      meals_consumed: [],
      behavior_notes: '',
      incident_occurred: false,
      incident_details: '',
      check_out_notes: '',
      checked_out_by_member_id: '',
    });
    setAvailableFamilyMembers([]);
  };

  const fetchFamilyMembers = async (memberId: string) => {
    try {
      setLoadingFamilyMembers(true);
      console.log('Fetching family members for member ID:', memberId);
      const familyMembers = await memberService.getFamilyMembersByMemberId(memberId);
      console.log('Fetched family members:', familyMembers);
      setAvailableFamilyMembers(familyMembers);
    } catch (error) {
      console.error('Error fetching family members:', error);
      setAvailableFamilyMembers([]);
    } finally {
      setLoadingFamilyMembers(false);
    }
  };

  const handleBookingToggle = async (bookingId: string) => {
    const newSelection = selectedBookingIds.includes(bookingId)
      ? selectedBookingIds.filter(id => id !== bookingId)
      : [...selectedBookingIds, bookingId];

    setSelectedBookingIds(newSelection);

    // Fetch family members when first booking is selected
    if (newSelection.length > 0 && availableFamilyMembers.length === 0) {
      const firstBooking = todaysBookings.find((b: any) => b.id === newSelection[0]);
      if (firstBooking?.member_id) {
        await fetchFamilyMembers(firstBooking.member_id);
      }
    }
  };

  const resetAlertForm = () => {
    setAlertForm({
      booking_id: '',
      severity: AlertSeverity.INFO,
      alert_type: AlertType.OTHER,
      message: '',
      channels: ['call'],
    });
  };

  const resetWalkInForm = () => {
    setMemberNumber('');
    setMemberLookupResult(null);
    setWalkInForm({
      member_id: '',
      dependent_ids: [],
      parent_location: '',
      notes: '',
    });
    setWalkInError(null);
    setWalkInSuccess(false);
  };

  const handleCloseWalkInDialog = () => {
    setOpenWalkInDialog(false);
    resetWalkInForm();
  };

  const handleLookupMember = async () => {
    if (!memberNumber) {
      setWalkInError('Please enter a member number');
      return;
    }

    setLookupLoading(true);
    setWalkInError(null);
    try {
      const result = await kidsClubService.lookupMember(memberNumber);
      setMemberLookupResult(result);
      setWalkInForm({
        ...walkInForm,
        member_id: result.member.id,
      });
      // Just show info, don't block - staff can make exceptions
      if (result.dependents.length === 0) {
        setWalkInError('This member has no dependents registered');
      } else if (result.eligible_count === 0) {
        // Don't show as error - just a warning. Staff can still select and check in.
        console.log('Note: All dependents are outside recommended 3-12 age range');
      }
    } catch (error) {
      setWalkInError(getErrorMessage(error));
      setMemberLookupResult(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const handlePerformCheckIn = async () => {
    if (selectedBookingIds.length === 0) return;

    dispatch(performCheckInStart());
    try {
      // Check in each selected booking sequentially
      for (const bookingId of selectedBookingIds) {
        const checkInData: KidsClubCheckInFormData = {
          booking_id: bookingId,
          ...checkInForm,
        };
        const checkIn = await kidsClubService.performCheckIn(checkInData);
        dispatch(performCheckInSuccess(checkIn));
      }

      // Reset form and close dialog after all check-ins
      setSelectedBookingIds([]);
      setCheckInForm({
        check_in_notes: '',
        parent_location: '',
        checked_in_by_member_id: '',
      });
      setOpenCheckInDialog(false);
      loadCurrentCheckIns();
      loadTodaysBookings();
    } catch (error) {
      dispatch(performCheckInFailure(getErrorMessage(error)));
    }
  };

  const handlePerformCheckOut = async () => {
    if (!selectedCheckInId) return;

    dispatch(performCheckOutStart());
    try {
      const checkIn = await kidsClubService.performCheckOut(selectedCheckInId, checkOutForm);
      dispatch(performCheckOutSuccess(checkIn));
      // Close dialog immediately after successful checkout
      setOpenCheckOutDialog(false);
      // Reset form
      setCheckOutForm({
        checked_out_by_member_id: '',
        check_out_notes: '',
      });
    } catch (error) {
      dispatch(performCheckOutFailure(getErrorMessage(error)));
    }
  };

  const handleOpenCheckOutDialog = async (checkInId: string) => {
    setSelectedCheckInId(checkInId);

    // Find the check-in and store it
    const checkIn = currentCheckIns.find((ci: any) => ci.id === checkInId);
    console.log('Check-in data:', checkIn);
    setSelectedCheckIn(checkIn); // Store the check-in data

    // Try multiple paths to get the member_id
    let memberId = checkIn?.booking?.member_id || checkIn?.booking?.member?.id;

    // If still not found, try to find the booking in todaysBookings
    if (!memberId && checkIn?.booking_id) {
      const booking = todaysBookings.find((b: any) => b.id === checkIn.booking_id);
      console.log('Found booking in todaysBookings:', booking);
      memberId = booking?.member_id || booking?.member?.id;
    }

    console.log('Member ID for fetching family:', memberId);

    if (memberId) {
      await fetchFamilyMembers(memberId);
    } else {
      console.warn('Could not find member_id for check-in:', checkInId);
    }

    // Open dialog after fetching family members
    setOpenCheckOutDialog(true);
  };

  const handleUpdateLocation = async () => {
    if (!selectedCheckInId || !locationUpdate) return;

    dispatch(updateParentLocationStart());
    try {
      const checkIn = await kidsClubService.updateParentLocation(selectedCheckInId, locationUpdate);
      dispatch(updateParentLocationSuccess(checkIn));
      setOpenLocationDialog(false);
      setLocationUpdate('');
    } catch (error) {
      dispatch(updateParentLocationFailure(getErrorMessage(error)));
    }
  };

  const handleOpenLocationDialog = (checkInId: string, currentLocation?: string) => {
    setSelectedCheckInId(checkInId);
    setLocationUpdate(currentLocation || '');
    setOpenLocationDialog(true);
  };

  const handleOpenEditCheckInDialog = async (checkIn: any) => {
    console.log('Opening edit dialog for check-in:', checkIn);
    setEditingCheckIn(checkIn);

    // Format checked_in_at to datetime-local format (YYYY-MM-DDTHH:MM)
    let formattedCheckInTime = '';
    if (checkIn.checked_in_at) {
      const date = new Date(checkIn.checked_in_at);
      // Format to YYYY-MM-DDTHH:MM for datetime-local input
      formattedCheckInTime = date.toISOString().slice(0, 16);
    }

    setEditCheckInForm({
      current_parent_location: checkIn.current_parent_location || checkIn.parent_location_at_checkin || '',
      check_in_notes: checkIn.check_in_notes || '',
      checked_in_by_member_id: checkIn.checked_in_by_member_id || '',
      checked_in_at: formattedCheckInTime,
    });

    // Fetch family members for the "who dropped off" dropdown
    // Try multiple paths to get the member_id
    let memberId = checkIn.booking?.member_id;
    console.log('Member ID from booking:', memberId);
    console.log('Check-in booking object:', checkIn.booking);
    console.log('Check-in dependent object:', checkIn.dependent);

    // If not found in booking, try to get from dependent's primary member or household
    if (!memberId) {
      if (checkIn.dependent?.primary_member?.id) {
        memberId = checkIn.dependent.primary_member.id;
        console.log('Member ID from dependent.primary_member:', memberId);
      } else if (checkIn.dependent?.household_members && checkIn.dependent.household_members.length > 0) {
        // Use first non-dependent household member
        const primaryMember = checkIn.dependent.household_members.find((m: any) => m.member_type !== 'dependent');
        if (primaryMember) {
          memberId = primaryMember.id;
          console.log('Member ID from household_members:', memberId);
        }
      }
    }

    console.log('Final member ID to fetch family members:', memberId);
    if (memberId) {
      await fetchFamilyMembers(memberId);
    } else {
      console.warn('No member ID found - cannot fetch family members');
      console.log('Full check-in structure:', JSON.stringify(checkIn, null, 2));
    }

    setOpenEditCheckInDialog(true);
  };

  const handleUpdateCheckIn = async () => {
    if (!editingCheckIn) return;

    dispatch(updateParentLocationStart());
    try {
      const updateData: any = {
        current_parent_location: editCheckInForm.current_parent_location,
        check_in_notes: editCheckInForm.check_in_notes,
      };

      if (editCheckInForm.checked_in_by_member_id) {
        updateData.checked_in_by_member_id = editCheckInForm.checked_in_by_member_id;
      }

      if (editCheckInForm.checked_in_at) {
        // Convert datetime-local format to ISO string
        updateData.checked_in_at = new Date(editCheckInForm.checked_in_at).toISOString();
      }

      const updatedCheckIn = await kidsClubService.updateCheckIn(editingCheckIn.id, updateData);

      dispatch(updateParentLocationSuccess(updatedCheckIn));
      setOpenEditCheckInDialog(false);
      setEditingCheckIn(null);
      loadCurrentCheckIns();
    } catch (error) {
      dispatch(updateParentLocationFailure(getErrorMessage(error)));
    }
  };

  const handleUndoCheckIn = async () => {
    if (!editingCheckIn) return;

    if (!window.confirm('Are you sure you want to undo this check-in? This will remove the check-in record and the child will need to be checked in again.')) {
      return;
    }

    dispatch(performCheckOutStart());
    try {
      // Delete the check-in by setting checked_out_at to remove it from the system
      // Or we could create a dedicated API endpoint for this
      const updatedCheckIn = await kidsClubService.performCheckOut(editingCheckIn.id, {
        check_out_notes: 'Check-in undone by staff - wrong child checked in',
      });

      dispatch(performCheckOutSuccess(updatedCheckIn));
      setOpenEditCheckInDialog(false);
      setEditingCheckIn(null);
      loadCurrentCheckIns();
      loadTodaysBookings();
    } catch (error) {
      dispatch(performCheckOutFailure(getErrorMessage(error)));
    }
  };

  const handleCreateAlert = async () => {
    if (!alertForm.booking_id || !alertForm.message) return;

    dispatch(createAlertStart());
    try {
      const alert = await kidsClubService.createAlert(alertForm);
      dispatch(createAlertSuccess(alert));
    } catch (error) {
      dispatch(createAlertFailure(getErrorMessage(error)));
    }
  };

  const handleOpenAlertDialog = (bookingId: string) => {
    setAlertForm({
      ...alertForm,
      booking_id: bookingId,
    });
    setOpenAlertDialog(true);
  };

  const handleOpenResolveDialog = (alertId: string) => {
    setSelectedAlertId(alertId);
    setResolveNotes('');
    setOpenResolveDialog(true);
  };

  const handleCloseResolveDialog = () => {
    setOpenResolveDialog(false);
    setSelectedAlertId(null);
    setResolveNotes('');
  };

  const handleResolveAlert = async () => {
    if (!selectedAlertId || !resolveNotes.trim()) {
      return;
    }

    dispatch(resolveAlertStart());
    try {
      await kidsClubService.resolveAlert(selectedAlertId, resolveNotes);
      dispatch(resolveAlertSuccess(selectedAlertId));
      handleCloseResolveDialog();
    } catch (error) {
      dispatch(resolveAlertFailure(getErrorMessage(error)));
    }
  };

  const handleWalkInSubmit = async () => {
    if (!walkInForm.member_id || walkInForm.dependent_ids.length === 0) {
      setWalkInError('Please select a member and at least one child');
      return;
    }

    setWalkInLoading(true);
    setWalkInError(null);
    try {
      // Create walk-in bookings sequentially for each dependent
      for (const dependentId of walkInForm.dependent_ids) {
        const walkInData: WalkInFormData = {
          member_id: walkInForm.member_id,
          dependent_id: dependentId,
          parent_location: walkInForm.parent_location,
          notes: walkInForm.notes,
        };
        await kidsClubService.createWalkIn(walkInData);
      }

      setWalkInSuccess(true);
      // Refresh the check-ins list and bookings
      loadCurrentCheckIns();
      loadTodaysBookings();
    } catch (error) {
      setWalkInError(getErrorMessage(error));
    } finally {
      setWalkInLoading(false);
    }
  };

  // Add Booking handlers
  const handleOpenAddBookingDialog = (date: string) => {
    setAddBookingForm({
      booking_date: date,
      dependent_ids: [],
      start_time: '',
      end_time: '',
      parent_location: '',
      special_instructions: '',
    });
    setAddBookingMemberNumber('');
    setAddBookingLookupResult(null);
    setAddBookingError(null);
    setOpenAddBookingDialog(true);
  };

  const handleCloseAddBookingDialog = () => {
    setOpenAddBookingDialog(false);
    resetAddBookingForm();
  };

  const resetAddBookingForm = () => {
    setAddBookingMemberNumber('');
    setAddBookingLookupResult(null);
    setAddBookingForm({
      booking_date: '',
      dependent_ids: [],
      start_time: '',
      end_time: '',
      parent_location: '',
      special_instructions: '',
    });
    setAddBookingError(null);
    setAddBookingSuccess(false);
  };

  const handleAddBookingLookupMember = async () => {
    if (!addBookingMemberNumber) {
      setAddBookingError('Please enter a member number');
      return;
    }

    setAddBookingLookupLoading(true);
    setAddBookingError(null);
    try {
      const result = await kidsClubService.lookupMember(addBookingMemberNumber);
      setAddBookingLookupResult(result);
      if (result.dependents.length === 0) {
        setAddBookingError('This member has no dependents registered');
      }
    } catch (error) {
      setAddBookingError(getErrorMessage(error));
      setAddBookingLookupResult(null);
    } finally {
      setAddBookingLookupLoading(false);
    }
  };

  const handleAddBookingSubmit = async () => {
    if (addBookingForm.dependent_ids.length === 0 || !addBookingForm.booking_date ||
        !addBookingForm.start_time || !addBookingForm.end_time) {
      setAddBookingError('Please fill in all required fields and select at least one child');
      return;
    }

    setAddBookingLoading(true);
    setAddBookingError(null);
    try {
      // Create bookings for all selected dependents SEQUENTIALLY to avoid race conditions
      for (const dependent_id of addBookingForm.dependent_ids) {
        const bookingData = {
          dependent_id,
          booking_date: addBookingForm.booking_date,
          start_time: addBookingForm.start_time,
          end_time: addBookingForm.end_time,
          parent_location: addBookingForm.parent_location,
          special_instructions: addBookingForm.special_instructions,
        };
        await kidsClubService.createStaffBooking(bookingData);
      }

      setAddBookingSuccess(true);

      // Refresh calendar and bookings
      fetchSummaryData(currentView);
      loadTodaysBookings();
      loadPendingBookings();

      // If the expanded date matches the booking date, refresh the expanded data
      if (expandedDate === addBookingForm.booking_date) {
        fetchExpandedDayData(addBookingForm.booking_date);
      }
    } catch (error) {
      setAddBookingError(getErrorMessage(error));
    } finally {
      setAddBookingLoading(false);
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'error';
      case 'urgent':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              <ChildCareIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Kids Club Staff Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage check-ins, check-outs, and emergency alerts
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<LoginIcon />}
              onClick={() => setOpenCheckInDialog(true)}
            >
              Check In Child
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ChildCareIcon />}
              onClick={() => setOpenWalkInDialog(true)}
            >
              Walk-In Check-In
            </Button>
          </Stack>
        </Box>

        {checkInError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearCheckInError())}>
            {checkInError}
          </Alert>
        )}

        {alertError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearAlertError())}>
            {alertError}
          </Alert>
        )}

        {checkInSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(clearCheckInSuccess())}>
            Operation completed successfully!
          </Alert>
        )}

        {alertSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(clearAlertSuccess())}>
            Alert operation completed successfully!
          </Alert>
        )}

        {walkInError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setWalkInError(null)}>
            {walkInError}
          </Alert>
        )}

        {walkInSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setWalkInSuccess(false)}>
            Walk-in check-in completed successfully!
          </Alert>
        )}

        {addBookingSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAddBookingSuccess(false)}>
            Booking created successfully!
          </Alert>
        )}

        {addBookingError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddBookingError(null)}>
            {addBookingError}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarTodayIcon fontSize="small" />
                  Booking Calendar
                </Box>
              }
            />
            <Tab
              label={
                <Badge badgeContent={currentCheckIns.length} color="primary">
                  Current Check-Ins
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={unresolvedAlerts.length} color="error">
                  Emergency Alerts
                </Badge>
              }
            />
          </Tabs>
        </Box>

        {/* Tab 0: Booking Calendar */}
        {tabValue === 0 && (
          <Box>
            {/* Quick Date Filters */}
            <Box sx={{ mb: 3 }}>
              <QuickDateFilters
                currentView={currentView}
                onViewChange={handleViewChange}
                onNavigate={handleNavigate}
                showNavigation={true}
              />
            </Box>

            {/* Error Alert */}
            {calendarError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setCalendarError(null)}>
                {calendarError}
              </Alert>
            )}

            {/* Loading State */}
            {calendarLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Calendar Views */}
            {!calendarLoading && (
              <>
                {/* Calendar Overview */}
                {!calendarDetailedView && (
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                      {getOverviewLabel()}
                    </Typography>
                    <WeeklyCapacityView
                      summaryData={summaryData}
                      loading={false}
                      error={calendarError}
                      onDateClick={handleDateClick}
                      isMonthView={currentView === 'month'}
                      expandedDate={expandedDate}
                      expandedDayData={expandedDayData}
                      loadingExpanded={loadingExpanded}
                      onConfirmBooking={handleConfirmBooking}
                      onCancelBooking={handleCancelBookingClick}
                      onReinstateBooking={handleReinstateBooking}
                      onCheckIn={(bookingId) => {
                        const booking = expandedDayData?.bookings?.find((b: any) => b.id === bookingId);
                        if (booking) {
                          setSelectedBookingIds([bookingId]);
                          setCheckInForm({
                            check_in_notes: '',
                            parent_location: booking.parent_location || '',
                            checked_in_by_member_id: '',
                          });
                          if (booking.member_id) {
                            fetchFamilyMembers(booking.member_id);
                          }
                          setOpenCheckInDialog(true);
                        }
                      }}
                      onCheckOut={handleOpenCheckOutDialog}
                      onAddBooking={handleOpenAddBookingDialog}
                      onEditBooking={handleEditBooking}
                    />
                  </Box>
                )}

                {/* Detailed Breakdown */}
                {calendarDetailedView && detailedData.length > 0 && (
                  <Box>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6">
                        Daily Booking Details
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setCalendarDetailedView(false);
                          fetchSummaryData(currentView);
                        }}
                      >
                        Back to Weekly View
                      </Button>
                    </Box>
                    {detailedData.map((day) => (
                      <DailyBookingBreakdown
                        key={day.date}
                        date={day.date}
                        dayOfWeek={day.day_of_week}
                        isClosed={day.is_closed}
                        capacity={day.capacity}
                        bookings={day.bookings || []}
                        daySchedule={day.day_schedule}
                        defaultExpanded={true}
                        onConfirmBooking={handleConfirmBooking}
                        onCancelBooking={handleCancelBookingClick}
                        onReinstateBooking={handleReinstateBooking}
                        onEditBooking={handleEditBooking}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* Tab 1: Current Check-Ins */}
        {tabValue === 1 && (
          <>
            {/* Pending Bookings - Require Approval */}
            {pendingBookings.length > 0 && (
              <Card sx={{ mb: 3, borderColor: 'warning.main', borderWidth: 2, borderStyle: 'solid' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Badge badgeContent={pendingBookings.length} color="warning">
                      <PeopleIcon color="warning" fontSize="large" />
                    </Badge>
                    <Typography variant="h6" color="warning.main">
                      Pending Bookings - Require Approval ({pendingBookings.length})
                    </Typography>
                  </Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    These bookings were made by members and need staff confirmation before check-in.
                  </Alert>
                  {loadingPendingBookings && isInitialLoad ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Booking Date</TableCell>
                            <TableCell>Child Name</TableCell>
                            <TableCell>Parent Name</TableCell>
                            <TableCell>Time Slot</TableCell>
                            <TableCell>Parent Location</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingBookings.map((booking: any) => (
                            <TableRow key={booking.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {(() => {
                                    const [year, month, day] = booking.booking_date.split('-').map(Number);
                                    return new Date(year, month - 1, day).toLocaleDateString();
                                  })()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {booking.dependent?.first_name} {booking.dependent?.last_name}
                                </Typography>
                                {booking.dependent?.age && (
                                  <Typography variant="caption" color="text.secondary">
                                    Age {booking.dependent.age}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {booking.member?.first_name} {booking.member?.last_name}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap>
                                  {booking.start_time} - {booking.end_time}
                                </Typography>
                              </TableCell>
                              <TableCell>{booking.parent_location || 'Not specified'}</TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={() => handleConfirmBooking(booking.id)}
                                  >
                                    Confirm
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<CancelIcon />}
                                    onClick={() => handleRejectBookingClick(booking.id)}
                                  >
                                    Reject
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Today's Bookings - Confirmed & Awaiting Check-In */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Confirmed Bookings - Awaiting Check-In ({todaysBookings.filter((b: any) => b.status === 'confirmed').length})
                </Typography>
                {loadingBookings && isInitialLoad ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : todaysBookings.filter((b: any) => b.status === 'confirmed').length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No confirmed bookings for today.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Child Name</TableCell>
                          <TableCell>Parent Name</TableCell>
                          <TableCell>Time Slot</TableCell>
                          <TableCell>Parent Location</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {todaysBookings.filter((b: any) => b.status === 'confirmed').map((booking) => (
                          <TableRow key={booking.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {booking.dependent?.first_name} {booking.dependent?.last_name}
                              </Typography>
                              {booking.dependent?.age && (
                                <Typography variant="caption" color="text.secondary">
                                  Age {booking.dependent.age}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {booking.member?.first_name} {booking.member?.last_name}
                            </TableCell>
                            <TableCell>
                              {booking.start_time} - {booking.end_time}
                            </TableCell>
                            <TableCell>{booking.parent_location || 'Not specified'}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<LoginIcon />}
                                  onClick={() => {
                                    setSelectedBookingIds([booking.id]);
                                    setCheckInForm({
                                      check_in_notes: '',
                                      parent_location: booking.parent_location || '',
                                      checked_in_by_member_id: '',
                                    });
                                    if (booking.member_id) {
                                      fetchFamilyMembers(booking.member_id);
                                    }
                                    setOpenCheckInDialog(true);
                                  }}
                                >
                                  Check In
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<CancelIcon />}
                                  onClick={() => handleCancelBookingClick(booking.id)}
                                >
                                  Cancel
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            {/* Currently Checked In Children */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Currently Checked In ({currentCheckIns.length})
                </Typography>
              {currentCheckIns.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No children currently checked in.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Child</TableCell>
                        <TableCell>Check-In Time</TableCell>
                        <TableCell>Dropped Off By</TableCell>
                        <TableCell>Parent Location</TableCell>
                        <TableCell>Emergency Contact</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentCheckIns.map((checkIn) => (
                        <TableRow key={checkIn.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {checkIn.dependent?.first_name}{' '}
                              {checkIn.dependent?.last_name}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatTime(checkIn.checked_in_at)}</TableCell>
                          <TableCell>
                            {checkIn.checked_in_by_member ? (
                              <Typography variant="body2">
                                {checkIn.checked_in_by_member.first_name} {checkIn.checked_in_by_member.last_name}
                                {checkIn.checked_in_by_member.relationship && (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                    ({checkIn.checked_in_by_member.relationship})
                                  </Typography>
                                )}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Not specified
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOnIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {checkIn.current_parent_location || checkIn.parent_location_at_checkin || 'Unknown'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {checkIn.checked_in_by_member?.phone ? (
                              <Typography variant="body2" fontWeight="medium">
                                {checkIn.checked_in_by_member.phone}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Not specified
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {checkIn.check_in_notes || 'No notes'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title="Edit Check-In">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenEditCheckInDialog(checkIn)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Create Alert">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() =>
                                    handleOpenAlertDialog(checkIn.booking_id)
                                  }
                                >
                                  <AlertIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Check Out">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleOpenCheckOutDialog(checkIn.id)}
                                >
                                  <LogoutIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel Booking">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleCancelBookingClick(checkIn.booking_id)}
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
          </>
        )}

        {/* Tab 2: Emergency Alerts */}
        {tabValue === 2 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Unresolved Alerts ({unresolvedAlerts.length})
              </Typography>
              {unresolvedAlerts.length === 0 ? (
                <Alert severity="success">
                  <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  No unresolved alerts. All clear!
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {unresolvedAlerts.map((alert) => (
                    <Card key={alert.id} variant="outlined">
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 8 }}>
                            <Stack spacing={1}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={alert.severity.toUpperCase()}
                                  color={getSeverityColor(alert.severity) as any}
                                  size="small"
                                />
                                <Chip
                                  label={alert.alert_type.replace('_', ' ').toUpperCase()}
                                  variant="outlined"
                                  size="small"
                                />
                                {alert.acknowledged && (
                                  <Chip
                                    label="ACKNOWLEDGED"
                                    color="info"
                                    size="small"
                                    icon={<CheckCircleIcon />}
                                  />
                                )}
                              </Box>
                              {alert.dependent && (
                                <Typography variant="h6" fontWeight="bold" color="primary">
                                  Child: {alert.dependent.first_name} {alert.dependent.last_name}
                                </Typography>
                              )}
                              <Typography variant="body1" fontWeight="medium">
                                {alert.message}
                              </Typography>

                              {alert.check_in?.checked_in_by_member && (
                                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                                  <Typography variant="body2" fontWeight="medium" color="text.secondary">
                                    Checked In By:
                                  </Typography>
                                  <Typography variant="body2">
                                    {alert.check_in.checked_in_by_member.first_name} {alert.check_in.checked_in_by_member.last_name}
                                  </Typography>
                                  {alert.check_in.checked_in_by_member.phone && (
                                    <Typography variant="body2" color="primary" fontWeight="medium">
                                      📞 {alert.check_in.checked_in_by_member.phone}
                                    </Typography>
                                  )}
                                  {alert.check_in.parent_location && (
                                    <Typography variant="body2" color="text.secondary">
                                      📍 Location: {alert.check_in.parent_location}
                                    </Typography>
                                  )}
                                </Box>
                              )}

                              <Typography variant="caption" color="text.secondary">
                                Created: {formatTime(alert.created_at)}
                              </Typography>
                              {alert.acknowledged && alert.parent_eta && (
                                <Typography variant="body2" color="primary">
                                  Parent ETA: {alert.parent_eta}
                                </Typography>
                              )}
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                  Alert Channels:
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  {alert.channels_used?.includes('call') && <Chip label="Call" size="small" color="info" />}
                                  {alert.sms_sent && <Chip label="SMS Sent" size="small" color="success" />}
                                  {alert.email_sent && <Chip label="Email Sent" size="small" color="success" />}
                                  {alert.push_sent && <Chip label="Push Sent" size="small" color="success" />}
                                </Stack>
                              </Box>
                            </Stack>
                          </Grid>
                          <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleOpenResolveDialog(alert.id)}
                              disabled={alertLoading}
                            >
                              Resolve
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}

              {/* Resolved Alerts Section */}
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="h6" gutterBottom>
                  Resolved Alerts (Today) ({resolvedAlerts.length})
                </Typography>
                {loadingResolvedAlerts && isInitialLoad ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : resolvedAlerts.length === 0 ? (
                  <Alert severity="info">No alerts have been resolved today.</Alert>
                ) : (
                  <Stack spacing={2}>
                    {resolvedAlerts.map((alert) => (
                      <Card key={alert.id} variant="outlined" sx={{ borderColor: 'success.main', bgcolor: 'success.50' }}>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                              <Stack spacing={1}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label="RESOLVED"
                                    color="success"
                                    size="small"
                                    icon={<CheckCircleIcon />}
                                  />
                                  <Chip
                                    label={alert.severity.toUpperCase()}
                                    color={getSeverityColor(alert.severity) as any}
                                    size="small"
                                  />
                                  <Chip
                                    label={alert.alert_type.replace('_', ' ').toUpperCase()}
                                    variant="outlined"
                                    size="small"
                                  />
                                </Box>
                                {alert.dependent && (
                                  <Typography variant="h6" fontWeight="bold" color="primary">
                                    Child: {alert.dependent.first_name} {alert.dependent.last_name}
                                  </Typography>
                                )}
                                <Typography variant="body1" fontWeight="medium">
                                  {alert.message}
                                </Typography>

                                {alert.check_in?.checked_in_by_member && (
                                  <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                                    <Typography variant="body2" fontWeight="medium" color="text.secondary">
                                      Checked In By:
                                    </Typography>
                                    <Typography variant="body2">
                                      {alert.check_in.checked_in_by_member.first_name} {alert.check_in.checked_in_by_member.last_name}
                                    </Typography>
                                    {alert.check_in.checked_in_by_member.phone && (
                                      <Typography variant="body2" color="primary" fontWeight="medium">
                                        📞 {alert.check_in.checked_in_by_member.phone}
                                      </Typography>
                                    )}
                                    {alert.check_in.parent_location && (
                                      <Typography variant="body2" color="text.secondary">
                                        📍 Location: {alert.check_in.parent_location}
                                      </Typography>
                                    )}
                                  </Box>
                                )}

                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    Created: {formatTime(alert.created_at)}
                                  </Typography>
                                  {alert.resolved_at && (
                                    <Typography variant="caption" color="success.main" fontWeight="medium" sx={{ display: 'block' }}>
                                      Resolved: {formatTime(alert.resolved_at)}
                                    </Typography>
                                  )}
                                </Box>

                                {alert.resolution_notes && (
                                  <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, borderLeft: 3, borderColor: 'success.main' }}>
                                    <Typography variant="body2" fontWeight="medium" color="text.secondary">
                                      Resolution Notes:
                                    </Typography>
                                    <Typography variant="body2">
                                      {alert.resolution_notes}
                                    </Typography>
                                  </Box>
                                )}

                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                    Alert Channels:
                                  </Typography>
                                  <Stack direction="row" spacing={1} flexWrap="wrap">
                                    {alert.channels_used?.includes('call') && <Chip label="Call" size="small" color="info" />}
                                    {alert.sms_sent && <Chip label="SMS Sent" size="small" color="success" />}
                                    {alert.email_sent && <Chip label="Email Sent" size="small" color="success" />}
                                    {alert.push_sent && <Chip label="Push Sent" size="small" color="success" />}
                                  </Stack>
                                </Box>
                              </Stack>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>
            </CardContent>
          </Card>
        )}


        {/* Check-In Dialog */}
        <Dialog open={openCheckInDialog} onClose={() => setOpenCheckInDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Check In Children
            {selectedBookingIds.length > 0 && (
              <Chip
                label={`${selectedBookingIds.length} selected`}
                color="primary"
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Select bookings to check in:
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {todaysBookings.filter((b: any) => b.status === 'confirmed').map((booking) => (
                    <Box
                      key={booking.id}
                      sx={{
                        p: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedBookingIds.includes(booking.id)}
                            onChange={() => handleBookingToggle(booking.id)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">
                              {booking.dependent?.first_name} {booking.dependent?.last_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {booking.start_time} to {booking.end_time}
                              {booking.member && ` • ${booking.member.first_name} ${booking.member.last_name}`}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  ))}
                </Paper>
              </Box>

              {selectedBookingIds.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Checked In By (Family Member)</InputLabel>
                  <Select
                    value={checkInForm.checked_in_by_member_id || ''}
                    label="Checked In By (Family Member)"
                    onChange={(e) => setCheckInForm({ ...checkInForm, checked_in_by_member_id: e.target.value })}
                    disabled={loadingFamilyMembers}
                  >
                    <MenuItem value="">
                      <em>Not Specified</em>
                    </MenuItem>
                    {(() => {
                      const firstSelectedBooking = todaysBookings.find((b: any) => b.id === selectedBookingIds[0]);
                      return firstSelectedBooking?.member ? (
                        <MenuItem key={firstSelectedBooking.member.id} value={firstSelectedBooking.member.id}>
                          {firstSelectedBooking.member.first_name} {firstSelectedBooking.member.last_name} (Primary Member)
                        </MenuItem>
                      ) : null;
                    })()}
                    {availableFamilyMembers.map((familyMember: any) => (
                      <MenuItem key={familyMember.id} value={familyMember.id}>
                        {familyMember.first_name} {familyMember.last_name} ({familyMember.relationship || 'Family Member'})
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingFamilyMembers && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Loading family members...
                    </Typography>
                  )}
                </FormControl>
              )}

              <TextField
                label="Parent Location"
                fullWidth
                value={checkInForm.parent_location}
                onChange={(e) => setCheckInForm({ ...checkInForm, parent_location: e.target.value })}
                placeholder="e.g., Tennis Courts, Golf Course"
              />
              <TextField
                label="Check-In Notes"
                multiline
                rows={3}
                fullWidth
                value={checkInForm.check_in_notes}
                onChange={(e) => setCheckInForm({ ...checkInForm, check_in_notes: e.target.value })}
                placeholder="Any notes about the child's arrival..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenCheckInDialog(false);
              setSelectedBookingIds([]);
            }}>
              Cancel
            </Button>
            <Button
              onClick={handlePerformCheckIn}
              variant="contained"
              disabled={checkInLoading || selectedBookingIds.length === 0}
              startIcon={checkInLoading ? <CircularProgress size={20} /> : <LoginIcon />}
            >
              {checkInLoading
                ? `Checking In ${selectedBookingIds.length}...`
                : `Check In ${selectedBookingIds.length > 1 ? `${selectedBookingIds.length} Children` : '1 Child'}`
              }
            </Button>
          </DialogActions>
        </Dialog>

        {/* Check-Out Dialog */}
        <Dialog open={openCheckOutDialog} onClose={() => setOpenCheckOutDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Check Out Child</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Checked Out By (Family Member)</InputLabel>
                <Select
                  value={checkOutForm.checked_out_by_member_id || ''}
                  label="Checked Out By (Family Member)"
                  onChange={(e) => setCheckOutForm({ ...checkOutForm, checked_out_by_member_id: e.target.value })}
                  disabled={loadingFamilyMembers}
                >
                  <MenuItem value="">
                    <em>Not Specified</em>
                  </MenuItem>
                  {(() => {
                    console.log('Dropdown - selectedCheckIn:', selectedCheckIn);
                    console.log('Dropdown - availableFamilyMembers:', availableFamilyMembers);

                    // Get the child being checked out (exclude from dropdown)
                    const dependentId = selectedCheckIn?.dependent?.id;

                    // Get the child's actual parent/guardian (from dependent.primary_member)
                    const dependentOwner = selectedCheckIn?.dependent?.primary_member;

                    // Get primary member from booking (who created the booking)
                    const bookingOwner = selectedCheckIn?.booking?.member;

                    // Get who checked in the child
                    const checkedInBy = selectedCheckIn?.checked_in_by_member;

                    const options = [];
                    const addedIds = new Set();

                    // Exclude the child being checked out
                    if (dependentId) {
                      addedIds.add(dependentId);
                    }

                    // Always show the dependent's primary member first (the actual parent/guardian)
                    if (dependentOwner && !addedIds.has(dependentOwner.id)) {
                      options.push(
                        <MenuItem key={dependentOwner.id} value={dependentOwner.id}>
                          {dependentOwner.first_name} {dependentOwner.last_name} (Parent/Guardian)
                        </MenuItem>
                      );
                      addedIds.add(dependentOwner.id);
                    }

                    // Show booking owner if different from dependent owner
                    if (bookingOwner && !addedIds.has(bookingOwner.id)) {
                      options.push(
                        <MenuItem key={bookingOwner.id} value={bookingOwner.id}>
                          {bookingOwner.first_name} {bookingOwner.last_name} (Booking Owner)
                        </MenuItem>
                      );
                      addedIds.add(bookingOwner.id);
                    }

                    // Show who checked in the child if different from above
                    if (checkedInBy && !addedIds.has(checkedInBy.id)) {
                      options.push(
                        <MenuItem key={checkedInBy.id} value={checkedInBy.id}>
                          {checkedInBy.first_name} {checkedInBy.last_name} (Checked In By)
                        </MenuItem>
                      );
                      addedIds.add(checkedInBy.id);
                    }

                    // Add household members from check-in data (adults only, backend filters out dependents)
                    const householdMembers = selectedCheckIn?.dependent?.household_members || [];
                    householdMembers.forEach((hm: any) => {
                      if (!addedIds.has(hm.id)) {
                        options.push(
                          <MenuItem key={hm.id} value={hm.id}>
                            {hm.first_name} {hm.last_name} ({hm.relationship_to_primary || 'Household Member'})
                          </MenuItem>
                        );
                        addedIds.add(hm.id);
                      }
                    });

                    // Also add any family members fetched separately (legacy support)
                    availableFamilyMembers.forEach((fm: any) => {
                      if (!addedIds.has(fm.id)) {
                        options.push(
                          <MenuItem key={fm.id} value={fm.id}>
                            {fm.first_name} {fm.last_name} ({fm.relationship || 'Family Member'})
                          </MenuItem>
                        );
                        addedIds.add(fm.id);
                      }
                    });

                    return options;
                  })()}
                </Select>
                {loadingFamilyMembers && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Loading family members...
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Meals Consumed</InputLabel>
                <Select
                  multiple
                  value={checkOutForm.meals_consumed || []}
                  onChange={(e) =>
                    setCheckOutForm({
                      ...checkOutForm,
                      meals_consumed: e.target.value as string[],
                    })
                  }
                  renderValue={(selected) => (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Stack>
                  )}
                >
                  {availableMeals.map((meal) => (
                    <MenuItem key={meal} value={meal}>
                      {meal}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Behavior Notes"
                multiline
                rows={2}
                fullWidth
                value={checkOutForm.behavior_notes}
                onChange={(e) =>
                  setCheckOutForm({ ...checkOutForm, behavior_notes: e.target.value })
                }
                placeholder="How was the child's behavior today?"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkOutForm.incident_occurred || false}
                    onChange={(e) =>
                      setCheckOutForm({
                        ...checkOutForm,
                        incident_occurred: e.target.checked,
                      })
                    }
                  />
                }
                label="Incident Occurred"
              />

              {checkOutForm.incident_occurred && (
                <TextField
                  label="Incident Details"
                  multiline
                  rows={3}
                  fullWidth
                  value={checkOutForm.incident_details}
                  onChange={(e) =>
                    setCheckOutForm({ ...checkOutForm, incident_details: e.target.value })
                  }
                  placeholder="Describe the incident..."
                />
              )}

              <TextField
                label="Check-Out Notes"
                multiline
                rows={2}
                fullWidth
                value={checkOutForm.check_out_notes}
                onChange={(e) =>
                  setCheckOutForm({ ...checkOutForm, check_out_notes: e.target.value })
                }
                placeholder="Any additional notes for the parent..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCheckOutDialog(false)}>Cancel</Button>
            <Button
              onClick={handlePerformCheckOut}
              variant="contained"
              color="error"
              disabled={checkInLoading}
              startIcon={checkInLoading ? <CircularProgress size={20} /> : <LogoutIcon />}
            >
              {checkInLoading ? 'Checking Out...' : 'Check Out'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Update Location Dialog */}
        <Dialog open={openLocationDialog} onClose={() => setOpenLocationDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update Parent Location</DialogTitle>
          <DialogContent>
            <TextField
              label="Parent Location"
              fullWidth
              value={locationUpdate}
              onChange={(e) => setLocationUpdate(e.target.value)}
              placeholder="e.g., Tennis Courts, Golf Course, Dining Room"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenLocationDialog(false)}>Cancel</Button>
            <Button
              onClick={handleUpdateLocation}
              variant="contained"
              disabled={checkInLoading || !locationUpdate}
              startIcon={checkInLoading ? <CircularProgress size={20} /> : <LocationOnIcon />}
            >
              {checkInLoading ? 'Updating...' : 'Update Location'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Check-In Dialog */}
        <Dialog open={openEditCheckInDialog} onClose={() => setOpenEditCheckInDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Check-In Details</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {editingCheckIn && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Child: <strong>{editingCheckIn.dependent?.first_name} {editingCheckIn.dependent?.last_name}</strong>
                </Typography>
              )}

              <TextField
                label="Check-In Time"
                type="datetime-local"
                fullWidth
                value={editCheckInForm.checked_in_at}
                onChange={(e) => setEditCheckInForm({ ...editCheckInForm, checked_in_at: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <FormControl fullWidth>
                <InputLabel>Dropped Off By</InputLabel>
                <Select
                  value={editCheckInForm.checked_in_by_member_id}
                  label="Dropped Off By"
                  onChange={(e) => setEditCheckInForm({ ...editCheckInForm, checked_in_by_member_id: e.target.value })}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {availableFamilyMembers.map((member: any) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                      {member.relationship && ` (${member.relationship})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Parent Location"
                fullWidth
                value={editCheckInForm.current_parent_location}
                onChange={(e) => setEditCheckInForm({ ...editCheckInForm, current_parent_location: e.target.value })}
                placeholder="e.g., Tennis Courts, Golf Course, Dining Room"
              />

              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={editCheckInForm.check_in_notes}
                onChange={(e) => setEditCheckInForm({ ...editCheckInForm, check_in_notes: e.target.value })}
                placeholder="Any special notes or instructions"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleUndoCheckIn}
              color="error"
              variant="outlined"
              disabled={checkInLoading}
              sx={{ mr: 'auto' }}
            >
              Undo Check-In
            </Button>
            <Button onClick={() => { setOpenEditCheckInDialog(false); setEditingCheckIn(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateCheckIn}
              variant="contained"
              disabled={checkInLoading}
              startIcon={checkInLoading ? <CircularProgress size={20} /> : <EditIcon />}
            >
              {checkInLoading ? 'Updating...' : 'Update Check-In'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Alert Dialog */}
        <Dialog open={openAlertDialog} onClose={() => setOpenAlertDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create Emergency Alert</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={alertForm.severity}
                  label="Severity"
                  onChange={(e) =>
                    setAlertForm({
                      ...alertForm,
                      severity: e.target.value as AlertSeverity,
                    })
                  }
                >
                  <MenuItem value={AlertSeverity.INFO}>Info</MenuItem>
                  <MenuItem value={AlertSeverity.URGENT}>Urgent</MenuItem>
                  <MenuItem value={AlertSeverity.CRITICAL}>Critical</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Alert Type</InputLabel>
                <Select
                  value={alertForm.alert_type}
                  label="Alert Type"
                  onChange={(e) =>
                    setAlertForm({
                      ...alertForm,
                      alert_type: e.target.value as AlertType,
                    })
                  }
                >
                  <MenuItem value={AlertType.OTHER}>Other</MenuItem>
                  <MenuItem value={AlertType.PICKUP_REMINDER}>Pickup Reminder</MenuItem>
                  <MenuItem value={AlertType.BEHAVIORAL_ISSUE}>Behavioral Issue</MenuItem>
                  <MenuItem value={AlertType.MEDICAL_EMERGENCY}>Medical Emergency</MenuItem>
                  <MenuItem value={AlertType.INCIDENT}>Incident</MenuItem>
                  <MenuItem value={AlertType.PARENT_REQUEST}>Parent Request</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Message"
                multiline
                rows={4}
                fullWidth
                value={alertForm.message}
                onChange={(e) => setAlertForm({ ...alertForm, message: e.target.value })}
                placeholder="Enter the alert message for the parent..."
              />

              <FormControl fullWidth>
                <InputLabel>Notification Channels</InputLabel>
                <Select
                  multiple
                  value={alertForm.channels || []}
                  onChange={(e) =>
                    setAlertForm({
                      ...alertForm,
                      channels: e.target.value as string[],
                    })
                  }
                  renderValue={(selected) => (selected as string[]).join(', ')}
                >
                  <MenuItem value="call">Call</MenuItem>
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="push">Push Notification</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAlertDialog(false)}>Cancel</Button>
            <Button
              onClick={handleCreateAlert}
              variant="contained"
              color="warning"
              disabled={alertLoading || !alertForm.message}
              startIcon={alertLoading ? <CircularProgress size={20} /> : <NotificationsActiveIcon />}
            >
              {alertLoading ? 'Creating...' : 'Create Alert'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Resolve Alert Dialog */}
        <Dialog open={openResolveDialog} onClose={handleCloseResolveDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Resolve Emergency Alert</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Alert severity="info">
                Please provide detailed notes on how this alert was resolved. This information is important for record keeping and future reference.
              </Alert>
              <TextField
                label="Resolution Notes"
                multiline
                rows={6}
                fullWidth
                required
                value={resolveNotes}
                onChange={(e) => setResolveNotes(e.target.value)}
                placeholder="Describe how the alert was resolved, what actions were taken, and the outcome..."
                error={resolveNotes.trim() === ''}
                helperText={resolveNotes.trim() === '' ? 'Resolution notes are required' : ''}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseResolveDialog}>Cancel</Button>
            <Button
              onClick={handleResolveAlert}
              variant="contained"
              color="success"
              disabled={alertLoading || !resolveNotes.trim()}
              startIcon={alertLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {alertLoading ? 'Resolving...' : 'Resolve Alert'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Walk-In Check-In Dialog */}
        <Dialog open={openWalkInDialog} onClose={handleCloseWalkInDialog} maxWidth="md" fullWidth>
          <DialogTitle>Walk-In Check-In (Member Drop-In)</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              For club members who didn't pre-book. Enter the member number to lookup their eligible children.
            </Alert>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Member Lookup */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  label="Member Number"
                  fullWidth
                  required
                  value={memberNumber}
                  onChange={(e) => setMemberNumber(e.target.value)}
                  placeholder="Enter member number"
                  disabled={!!memberLookupResult}
                />
                <Button
                  variant="contained"
                  onClick={handleLookupMember}
                  disabled={lookupLoading || !memberNumber || !!memberLookupResult}
                  startIcon={lookupLoading ? <CircularProgress size={20} /> : <PeopleIcon />}
                  sx={{ minWidth: 120, height: 56 }}
                >
                  {lookupLoading ? 'Looking up...' : 'Lookup'}
                </Button>
              </Box>

              {/* Member Info (after lookup) */}
              {memberLookupResult && (
                <>
                  {/* Change Member Button - Prominent placement */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" color="success.main">
                      ✓ Member Found
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setMemberLookupResult(null);
                        setMemberNumber('');
                        setWalkInForm({ ...walkInForm, member_id: '', dependent_ids: [] });
                      }}
                    >
                      Change Member
                    </Button>
                  </Box>

                  <Card variant="outlined" sx={{ p: 2, borderColor: 'success.main', borderWidth: 2 }}>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">Name:</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {memberLookupResult.member.first_name} {memberLookupResult.member.last_name}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">Member #:</Typography>
                        <Typography variant="body1" fontWeight="medium">{memberLookupResult.member.member_number}</Typography>
                      </Grid>
                      {memberLookupResult.member.phone && (
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">Phone:</Typography>
                          <Typography variant="body1">{memberLookupResult.member.phone}</Typography>
                        </Grid>
                      )}
                      {memberLookupResult.member.email && (
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">Email:</Typography>
                          <Typography variant="body1" sx={{ fontSize: '0.9rem' }}>{memberLookupResult.member.email}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Card>

                  {memberLookupResult.dependents.length === 0 ? (
                    <Alert severity="warning">
                      This member has no dependents registered. Please add dependents to their profile first.
                    </Alert>
                  ) : (
                    <>
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Select Children to Check In
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {memberLookupResult.dependents.map((dependent: any) => (
                          <Card
                            key={dependent.id}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              borderWidth: walkInForm.dependent_ids.includes(dependent.id) ? 2 : 1,
                              borderColor: walkInForm.dependent_ids.includes(dependent.id) ? 'primary.main' : 'divider',
                              bgcolor: walkInForm.dependent_ids.includes(dependent.id) ? 'action.selected' : 'background.paper',
                              '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: 'action.hover',
                              },
                            }}
                            onClick={() => {
                              const isSelected = walkInForm.dependent_ids.includes(dependent.id);
                              setWalkInForm({
                                ...walkInForm,
                                dependent_ids: isSelected
                                  ? walkInForm.dependent_ids.filter((id) => id !== dependent.id)
                                  : [...walkInForm.dependent_ids, dependent.id],
                              });
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Checkbox
                                checked={walkInForm.dependent_ids.includes(dependent.id)}
                                sx={{ p: 0 }}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" fontWeight="medium">
                                  {dependent.first_name} {dependent.last_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {dependent.age !== null && dependent.age !== undefined
                                    ? `Age ${dependent.age}`
                                    : 'Age Unknown'}
                                  {!dependent.eligible && (
                                    <Chip
                                      label="Outside 3-12 range"
                                      size="small"
                                      color="warning"
                                      sx={{ ml: 1, height: 20 }}
                                    />
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                          </Card>
                        ))}
                      </Box>

                      {walkInForm.dependent_ids.length > 0 && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                          ✓ {walkInForm.dependent_ids.length} {walkInForm.dependent_ids.length === 1 ? 'child' : 'children'} selected for check-in
                        </Alert>
                      )}
                    </>
                  )}

                  <TextField
                    label="Parent Location"
                    fullWidth
                    value={walkInForm.parent_location}
                    onChange={(e) => setWalkInForm({ ...walkInForm, parent_location: e.target.value })}
                    placeholder="e.g., Tennis Courts, Golf Course, Dining Room"
                  />

                  <TextField
                    label="Notes"
                    multiline
                    rows={3}
                    fullWidth
                    value={walkInForm.notes}
                    onChange={(e) => setWalkInForm({ ...walkInForm, notes: e.target.value })}
                    placeholder="Any special instructions or notes..."
                  />
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseWalkInDialog}>Cancel</Button>
            <Button
              onClick={handleWalkInSubmit}
              variant="contained"
              color="secondary"
              disabled={walkInLoading || !memberLookupResult || walkInForm.dependent_ids.length === 0}
              startIcon={walkInLoading ? <CircularProgress size={20} /> : <ChildCareIcon />}
            >
              {walkInLoading
                ? 'Processing...'
                : walkInForm.dependent_ids.length > 1
                ? `Check In ${walkInForm.dependent_ids.length} Children`
                : 'Check In'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Booking Dialog */}
        <Dialog open={rejectDialogOpen} onClose={handleRejectDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Reject Booking</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This booking will be cancelled and the member will be notified of the rejection reason.
            </Alert>
            <TextField
              autoFocus
              margin="dense"
              label="Rejection Reason"
              type="text"
              fullWidth
              multiline
              rows={4}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a reason for rejecting this booking (e.g., Kids Club at capacity, child does not meet age requirements, etc.)"
              required
              helperText="The member will see this reason"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRejectDialogClose}>Cancel</Button>
            <Button
              onClick={handleRejectBookingConfirm}
              variant="contained"
              color="error"
              disabled={!rejectionReason.trim()}
              startIcon={<CancelIcon />}
            >
              Reject Booking
            </Button>
          </DialogActions>
        </Dialog>

        {/* Staff Cancel Booking Dialog */}
        <Dialog open={cancelDialogOpen} onClose={handleCancelDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Cancel Booking</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This booking will be cancelled and the member will be notified of the cancellation.
            </Alert>
            <TextField
              autoFocus
              margin="dense"
              label="Cancellation Reason"
              type="text"
              fullWidth
              multiline
              rows={4}
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              placeholder="Please provide a reason for cancelling this booking (e.g., Emergency closure, facility maintenance, staff shortage, etc.)"
              required
              helperText="The member will see this reason"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDialogClose}>Close</Button>
            <Button
              onClick={handleCancelBookingConfirm}
              variant="contained"
              color="error"
              disabled={!cancellationReason.trim()}
              startIcon={<CancelIcon />}
            >
              Cancel Booking
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Booking Dialog */}
        <Dialog open={openAddBookingDialog} onClose={handleCloseAddBookingDialog} maxWidth="md" fullWidth>
          <DialogTitle>Create New Booking (Phone/In-Person Request)</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              Create a booking for a member who called or visited in person. The booking will be auto-confirmed without requiring member approval.
            </Alert>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Member Lookup */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  label="Member Number"
                  fullWidth
                  required
                  value={addBookingMemberNumber}
                  onChange={(e) => setAddBookingMemberNumber(e.target.value)}
                  placeholder="Enter member number"
                  disabled={!!addBookingLookupResult}
                />
                <Button
                  variant="contained"
                  onClick={handleAddBookingLookupMember}
                  disabled={addBookingLookupLoading || !addBookingMemberNumber || !!addBookingLookupResult}
                  startIcon={addBookingLookupLoading ? <CircularProgress size={20} /> : <PeopleIcon />}
                  sx={{ minWidth: 120, height: 56 }}
                >
                  {addBookingLookupLoading ? 'Looking up...' : 'Lookup'}
                </Button>
              </Box>

              {/* Member Info (after lookup) */}
              {addBookingLookupResult && (
                <>
                  {/* Change Member Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" color="success.main">
                      ✓ Member Found
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setAddBookingLookupResult(null);
                        setAddBookingMemberNumber('');
                        setAddBookingForm({ ...addBookingForm, dependent_ids: [] });
                      }}
                    >
                      Change Member
                    </Button>
                  </Box>

                  <Card variant="outlined" sx={{ p: 2, borderColor: 'success.main', borderWidth: 2 }}>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">Name:</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {addBookingLookupResult.member.first_name} {addBookingLookupResult.member.last_name}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="body2" color="text.secondary">Member #:</Typography>
                        <Typography variant="body1" fontWeight="medium">{addBookingLookupResult.member.member_number}</Typography>
                      </Grid>
                      {addBookingLookupResult.member.phone && (
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">Phone:</Typography>
                          <Typography variant="body1">{addBookingLookupResult.member.phone}</Typography>
                        </Grid>
                      )}
                      {addBookingLookupResult.member.email && (
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="body2" color="text.secondary">Email:</Typography>
                          <Typography variant="body1" sx={{ fontSize: '0.9rem' }}>{addBookingLookupResult.member.email}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Card>

                  {addBookingLookupResult.dependents.length === 0 ? (
                    <Alert severity="warning">
                      This member has no dependents registered. Please add dependents to their profile first.
                    </Alert>
                  ) : (
                    <>
                      {/* Select Children */}
                      <FormControl fullWidth required>
                        <InputLabel>Select Children</InputLabel>
                        <Select
                          multiple
                          value={addBookingForm.dependent_ids}
                          label="Select Children"
                          onChange={(e) => setAddBookingForm({ ...addBookingForm, dependent_ids: e.target.value as string[] })}
                          renderValue={(selected) => {
                            if (selected.length === 0) return '';
                            const names = selected.map((id: string) => {
                              const dep = addBookingLookupResult.dependents.find((d: any) => d.id === id);
                              return dep ? `${dep.first_name} ${dep.last_name}` : '';
                            }).filter(Boolean);
                            return `${names.length} child${names.length !== 1 ? 'ren' : ''} selected`;
                          }}
                        >
                          {addBookingLookupResult.dependents.map((dependent: any) => (
                            <MenuItem key={dependent.id} value={dependent.id}>
                              <ListItemText
                                primary={`${dependent.first_name} ${dependent.last_name}`}
                                secondary={`Age ${dependent.age || 'Unknown'}${!dependent.eligible ? ' - Outside 3-12 range' : ''}`}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Booking Date */}
                      <TextField
                        label="Booking Date"
                        type="date"
                        fullWidth
                        required
                        value={addBookingForm.booking_date}
                        onChange={(e) => setAddBookingForm({ ...addBookingForm, booking_date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: new Date().toISOString().split('T')[0],
                        }}
                      />

                      {/* Time Selection */}
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          label="Start Time"
                          type="time"
                          fullWidth
                          required
                          value={addBookingForm.start_time}
                          onChange={(e) => setAddBookingForm({ ...addBookingForm, start_time: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="End Time"
                          type="time"
                          fullWidth
                          required
                          value={addBookingForm.end_time}
                          onChange={(e) => setAddBookingForm({ ...addBookingForm, end_time: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>

                      {/* Parent Location */}
                      <TextField
                        label="Parent Location"
                        fullWidth
                        value={addBookingForm.parent_location}
                        onChange={(e) => setAddBookingForm({ ...addBookingForm, parent_location: e.target.value })}
                        placeholder="e.g., Tennis Courts, Golf Course, Dining Room"
                      />

                      {/* Special Instructions */}
                      <TextField
                        label="Special Instructions"
                        multiline
                        rows={3}
                        fullWidth
                        value={addBookingForm.special_instructions}
                        onChange={(e) => setAddBookingForm({ ...addBookingForm, special_instructions: e.target.value })}
                        placeholder="Any special instructions or notes..."
                      />
                    </>
                  )}
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddBookingDialog}>Cancel</Button>
            <Button
              onClick={handleAddBookingSubmit}
              variant="contained"
              color="primary"
              disabled={addBookingLoading || !addBookingLookupResult || addBookingForm.dependent_ids.length === 0 ||
                        !addBookingForm.booking_date || !addBookingForm.start_time || !addBookingForm.end_time}
              startIcon={addBookingLoading ? <CircularProgress size={20} /> : <ChildCareIcon />}
            >
              {addBookingLoading ? 'Creating...' : 'Create Booking'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Booking Dialog */}
        <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Booking</DialogTitle>
          <DialogContent>
            {bookingToEdit && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Editing booking for: {bookingToEdit.dependent?.first_name} {bookingToEdit.dependent?.last_name}
                </Typography>

                {editError && (
                  <Alert severity="error">{editError}</Alert>
                )}

                <TextField
                  label="Booking Date"
                  type="date"
                  value={editForm.booking_date}
                  onChange={(e) => setEditForm({ ...editForm, booking_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />

                <TextField
                  label="Start Time"
                  type="time"
                  value={editForm.start_time}
                  onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />

                <TextField
                  label="End Time"
                  type="time"
                  value={editForm.end_time}
                  onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />

                <FormControl fullWidth>
                  <InputLabel>Session</InputLabel>
                  <Select
                    value={editForm.session}
                    label="Session"
                    onChange={(e) => setEditForm({ ...editForm, session: e.target.value })}
                  >
                    <MenuItem value="AM">Morning</MenuItem>
                    <MenuItem value="PM">Afternoon</MenuItem>
                    <MenuItem value="EVENING">Evening</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Parent Location"
                  value={editForm.parent_location}
                  onChange={(e) => setEditForm({ ...editForm, parent_location: e.target.value })}
                  fullWidth
                />

                <TextField
                  label="Special Instructions"
                  value={editForm.special_instructions}
                  onChange={(e) => setEditForm({ ...editForm, special_instructions: e.target.value })}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog} disabled={editLoading}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} variant="contained" disabled={editLoading}>
              {editLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default KidsClubStaffPage;
